import {
  ACTION_TYPE_INSURE_PACKAGE,
  ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
  ACTION_TYPE_SET_SERVICE_COLLECTION,
  ACTION_TYPE_SET_SHIP_FROM_LOCATION,
} from '../../../settings/automation-rules/constants/action-types';
import {
  OPERATOR_TYPE_DOES_NOT_INCLUDE,
  OPERATOR_TYPE_GREATER_THAN,
  OPERATOR_TYPE_INCLUDES,
  OPERATOR_TYPE_LESS_THAN,
  OPERATOR_TYPE_VALUE_EQUALS_FALSE,
} from '../../../../components/filter-editor/constants/filter-operator-type';
import { Providers } from '../../../../enums/providers';
import { InsuranceProviders } from '../../../../enums/insurance-providers';
import { PackageType } from '../../../../enums/package-type';
import { ResidentialIndicator } from '../../../../enums/residential-indicator';
import { FedexWalletService } from '../../../../enums/label-providers/fedex/fedex-walleted-service';
import { UspsWalletService } from '../../../../enums/label-providers/usps/usps-wallet-service';

import type { PreconfiguredRule } from './automation-playbook-types';
import { messages } from './messages';
import { CHEAPEST_SERVICE_COLLECTION_GUID } from '@shipstation/client-common/constants/service-collections';
import { DEFAULT_PACKAGE_ID } from '../../../../constants/packages';

/** Walleted LabelProviders are the only ones we use to pre-populate rules */
export const UspsProviderId = Providers.Stamps.labelProviderId;
export const FedExProviderId = Providers.FedExWalleted.labelProviderId;

export const AutomationPlaybooks = {
  CommercialAddressService: 'CommercialAddressService',
  InsureHighValueOrders: 'InsureHighValueOrders',
  RateShopperByMarketplace: 'RateShopperByMarketplace',
  CheapestRateShopper: 'CheapestRateShopper',
  ResidentialAddressService: 'ResidentialAddressService',
  ShipFromByStore: 'ShipFromByStore',
  ShippingServiceBy48States: 'ShippingServiceBy48States',
  ShippingServiceByAlohaStates: 'ShippingServiceByAlohaStates',
  ShippingServiceByOrderTag: 'ShippingServiceByOrderTag',
  ShippingServiceByWeight: 'ShippingServiceByWeight',
} as const;

export type AutomationPlaybook = (typeof AutomationPlaybooks)[keyof typeof AutomationPlaybooks];

export const PlaybookCategories = {
  DecidingHowToShip: 'DecidingHowToShip',
  FulfillingAtScale: 'FulfillingAtScale',
} as const;

export type PlaybookCategories = (typeof PlaybookCategories)[keyof typeof PlaybookCategories];

export const SHIP_FROM_STORE_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.FulfillingAtScale,
  playbook: AutomationPlaybooks.ShipFromByStore,
  ruleName: messages.shipFromStoreRuleName,
  filterConditions: [{ fieldType: 'StoreGuid', operator: 'includes', value: '', logicalOperator: 'AND' }],
  workflowFilters: [{ fieldName: 'StoreId', operator: OPERATOR_TYPE_INCLUDES, value: '' }],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_SHIP_FROM_LOCATION,
      intParameter: null,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_SHIP_FROM_LOCATION,
      parameter: null,
      intParameter: null,
    },
  ],
};

export const RATE_SHOPPER_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.FulfillingAtScale,
  playbook: AutomationPlaybooks.RateShopperByMarketplace,
  ruleName: messages.rateShopperRuleName,
  filterConditions: [{ fieldType: 'MarketplaceId', operator: 'includes', value: '', logicalOperator: 'AND' }],
  workflowFilters: [{ fieldName: 'MarketplaceId', operator: OPERATOR_TYPE_INCLUDES, value: '' }],
  orderRuleActions: [{ actionTypeId: ACTION_TYPE_SET_SERVICE_COLLECTION, parameter: null, isDeleted: false }],
  workflowActions: [{ actionTypeId: ACTION_TYPE_SET_SERVICE_COLLECTION, parameter: null, intParameter: null }],
};

export const CHEAPEST_RATE_SHOPPER_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.FulfillingAtScale,
  playbook: AutomationPlaybooks.CheapestRateShopper,
  ruleName: messages.cheapestRateShopperRuleName,
  filterConditions: [],
  workflowFilters: [],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_SERVICE_COLLECTION,
      parameter: `${CHEAPEST_SERVICE_COLLECTION_GUID},${DEFAULT_PACKAGE_ID}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_SERVICE_COLLECTION,
      parameter: `${CHEAPEST_SERVICE_COLLECTION_GUID},${DEFAULT_PACKAGE_ID}`,
      intParameter: null,
    },
  ],
};

export const ORDER_TAG_SHIPPING_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.ShippingServiceByOrderTag,
  ruleName: messages.orderTagShippingRuleName,
  filterConditions: [{ fieldType: 'OrderTags', operator: 'includes', value: '', logicalOperator: 'AND' }],
  workflowFilters: [{ fieldName: 'Tag', operator: OPERATOR_TYPE_INCLUDES, value: '' }],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: undefined,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: null,
      intParameter: null,
    },
  ],
};

export const INSURANCE_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.FulfillingAtScale,
  playbook: AutomationPlaybooks.InsureHighValueOrders,
  ruleName: messages.insureOrdersRuleName,
  filterConditions: [{ fieldType: 'OrderTotal', operator: 'greaterThan', value: '', logicalOperator: 'AND' }],
  workflowFilters: [{ fieldName: 'OrderTotal', operator: OPERATOR_TYPE_GREATER_THAN, value: '' }],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_INSURE_PACKAGE,
      intParameter: Number(InsuranceProviders.ParcelGuard.insuranceProviderId),
      parameter: null,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_INSURE_PACKAGE,
      intParameter: Number(InsuranceProviders.ParcelGuard.insuranceProviderId),
      parameter: null,
    },
  ],
};

export const SHIPPING_SERVICE_WEIGHT_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.ShippingServiceByWeight,
  ruleName: messages.shippingServiceWeightRuleName,
  filterConditions: [
    { fieldType: 'shipToCountryCode', operator: 'includes', value: 'US', logicalOperator: 'AND' },
    { fieldType: 'OrderWeight', operator: 'lessThan', value: 16, logicalOperator: 'AND' },
  ],
  workflowFilters: [
    { fieldName: 'IsInternational', operator: OPERATOR_TYPE_VALUE_EQUALS_FALSE, value: '' },
    { fieldName: 'OrderWeight', operator: OPERATOR_TYPE_LESS_THAN, value: '16' },
  ],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.GroundAdvantage},${PackageType.StandardPackage}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.GroundAdvantage},${PackageType.StandardPackage}`,
      intParameter: null,
    },
  ],
};

export const STATE_48_SHIPPING_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.ShippingServiceBy48States,
  ruleName: messages.state48ShippingRuleName,
  filterConditions: [
    { fieldType: 'shipToCountryCode', operator: 'includes', value: 'US', logicalOperator: 'AND' },
    { fieldType: 'shipToState', operator: 'doesNotInclude', value: 'US-AK,US-HI', logicalOperator: 'AND' },
  ],
  workflowFilters: [
    { fieldName: 'IsInternational', operator: OPERATOR_TYPE_VALUE_EQUALS_FALSE, value: '' },
    { fieldName: 'ShipToState', operator: OPERATOR_TYPE_DOES_NOT_INCLUDE, value: ['US-AK', 'US-HI'] },
  ],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.GroundAdvantage},${PackageType.StandardPackage}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.GroundAdvantage},${PackageType.StandardPackage}`,
      intParameter: null,
    },
  ],
};

export const ALOHA_STATE_SHIPPING_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.ShippingServiceByAlohaStates,
  ruleName: messages.alohaStateShippingTitle,
  filterConditions: [{ fieldType: 'shipToState', operator: 'includes', value: 'US-AK,US-HI', logicalOperator: 'AND' }],
  workflowFilters: [{ fieldName: 'ShipToState', operator: OPERATOR_TYPE_INCLUDES, value: ['US-AK', 'US-HI'] }],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.PriorityMail},${PackageType.StandardPackage}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${UspsProviderId},${UspsWalletService.PriorityMail},${PackageType.StandardPackage}`,
      intParameter: null,
    },
  ],
};

export const RESIDENTIAL_SHIPPING_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.ResidentialAddressService,
  ruleName: messages.residentialShippingRuleName,
  filterConditions: [
    { fieldType: 'residentialIndicator', operator: 'includes', value: ResidentialIndicator.R, logicalOperator: 'AND' },
    { fieldType: 'carrierId', operator: 'includes', value: FedExProviderId, logicalOperator: 'AND' },
  ],
  workflowFilters: [
    { fieldName: 'ResidentialIndicator', operator: OPERATOR_TYPE_INCLUDES, value: [ResidentialIndicator.R] },
    { fieldName: 'CarrierId', operator: OPERATOR_TYPE_INCLUDES, value: [FedExProviderId] },
  ],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${FedExProviderId},${FedexWalletService.HomeDelivery},${PackageType.StandardPackage}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${FedExProviderId},${FedexWalletService.HomeDelivery},${PackageType.StandardPackage}`,
      intParameter: null,
    },
  ],
};

export const COMMERCIAL_SHIPPING_RULE: PreconfiguredRule = {
  active: true,
  isNew: true,
  isTemplate: true,
  category: PlaybookCategories.DecidingHowToShip,
  playbook: AutomationPlaybooks.CommercialAddressService,
  ruleName: messages.commercialShippingRuleName,
  filterConditions: [
    { fieldType: 'residentialIndicator', operator: 'includes', value: ResidentialIndicator.C, logicalOperator: 'AND' },
    { fieldType: 'carrierId', operator: 'includes', value: FedExProviderId, logicalOperator: 'AND' },
  ],
  workflowFilters: [
    { fieldName: 'ResidentialIndicator', operator: OPERATOR_TYPE_INCLUDES, value: [ResidentialIndicator.C] },
    { fieldName: 'CarrierId', operator: OPERATOR_TYPE_INCLUDES, value: [FedExProviderId] },
  ],
  orderRuleActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${FedExProviderId},${FedexWalletService.Ground},${PackageType.StandardPackage}`,
      isDeleted: false,
    },
  ],
  workflowActions: [
    {
      actionTypeId: ACTION_TYPE_SET_CARRIER_SERVICE_PACKAGE,
      parameter: `${FedExProviderId},${FedexWalletService.Ground},${PackageType.StandardPackage}`,
      intParameter: null,
    },
  ],
};
